import type { ReactNode } from 'react'

// Fact — ATC-109 / `ATC-ADR-018`.
//
// A label/value pair: an 11px uppercase field label over a 14px value. Ported
// from Flight Deck's `Fact` (`detailPrimitives.tsx`), minus Tailwind.
//
// A note the contract makes twice and is worth repeating at the point of use:
// "NO VALUE" IS NOT "MISSING DATA". Render the real absent state in words —
// "No account", "Never resolved" — never an em-dash, which reads as a data
// problem. And never `text.light.disabled` (#A19F9D, 2.64:1) for the label:
// that colour is conformant only for genuinely disabled controls, and a
// micro-label is content.

export interface FactProps {
  label: string
  children: ReactNode
}

export function Fact({ label, children }: FactProps) {
  return (
    <div className="rx-fact">
      <dt className="rx-fact-label">{label}</dt>
      <dd className="rx-fact-value">{children}</dd>
    </div>
  )
}

/**
 * The `<dl>` wrapper for a group of `Fact`s. Required, not optional styling: a
 * `<dt>`/`<dd>` pair outside a definition list is invalid HTML and screen
 * readers lose the label/value association.
 */
export function FactList({ children }: { children: ReactNode }) {
  return <dl className="rx-fact-list">{children}</dl>
}

/**
 * A monospace identifier or timestamp — a customer environment ID, a migration
 * ID, a serial number, an ISO timestamp.
 *
 * Never truncated: a GUID that overflows its column is worse than one that
 * wraps, and full identifiers are what an operator copies into a support
 * ticket or a `curl`.
 *
 * A LICENSE TOKEN IS NOT AN IDENTIFIER AND NEVER GOES HERE. Tokens are SHA-256
 * hashes at rest (`ATC-ADR-011`), the operator action is rotation rather than
 * re-display, and the one raw token the console ever sees — the one-time reveal
 * at issuance or rotation — has its own modal.
 */
export function Mono({ children }: { children: ReactNode }) {
  return <span className="rx-mono">{children}</span>
}
